import { ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'theme-ui';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { theme } from './theme';
import App from './App';
import store from './state/store';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { Global, css } from '@emotion/react';
import { fontFace } from './fonts/fonts';
import config from './config';
import { DoppioProvider } from '@odekoteam/doppio';
import { initLDProvider } from './services/launchDarklyService';
import { LaunchDarklyIdentity } from './services/LaunchDarklyIdentity';
import { initDatadogLogs } from './services/datadog';
import {
  applyPwaManifest,
  isPwaContext,
  watchPwaLaunch,
} from './utils/pwa';

// Start browser logging before anything else renders, so uncaught errors
// during the first paint are captured too. No-ops without a client token.
initDatadogLogs();

// Record that this session launched as an installed PWA before any auth redirect
// can strip the standalone signal — used to route back to Putaway after login.
watchPwaLaunch();

let persistor = persistStore(store);

const rootEl = document.getElementById('root');
const AUTH0_DOMAIN = config().auth0_domain;
const AUTH0_CLIENT_ID = config().auth0_client_id;
const AUTH0_AUDIENCE = config().auth0_audience;

const Auth0ProviderWithRedirectCallback = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const navigate = useNavigate();
  const onRedirectCallback = (appState?: AppState): void => {
    // The installed PWA launches at /putaway-app (see manifest start_url), but an
    // Auth0 login round-trips through an external browser and both appState and
    // the live standalone signal can be lost by the time the callback runs —
    // falling back to the web default of /deliveries. isPwaContext() reads the
    // flag persisted at launch, so an installed app returns to Putaway.
    // Keep this path in sync with start_url in public/manifest.webmanifest.
    const fallback = isPwaContext() ? '/putaway-app' : '/deliveries';
    navigate(appState?.returnTo || fallback);
  };
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

const renderApp = async (): Promise<void> => {
  const LDProvider = await initLDProvider();
  const root = createRoot(rootEl!);

  const doppioParams = { applicationName: 'logistics-web-app' };
  root.render(
    <StrictMode>
      <Provider store={store}>
        <DoppioProvider {...doppioParams}>
          <BrowserRouter>
            <ThemeProvider theme={theme}>
              <Global
                styles={css`
                  ${fontFace}
                  @media print {
                    #packing-table {
                      padding: 30px;
                    }
                  }
                `}
              />
              <Auth0ProviderWithRedirectCallback>
                <PersistGate
                  loading={<div>Loading...</div>}
                  persistor={persistor}
                >
                  <LDProvider>
                    <LaunchDarklyIdentity>
                      <App />
                    </LaunchDarklyIdentity>
                  </LDProvider>
                </PersistGate>
              </Auth0ProviderWithRedirectCallback>
            </ThemeProvider>
          </BrowserRouter>
        </DoppioProvider>
      </Provider>
    </StrictMode>,
  );
};

// Set the installable-app tags for this page and environment before render.
// App re-applies them on every navigation.
applyPwaManifest(window.location.pathname);

renderApp();

// Register the service worker that makes the app installable as a PWA. Only in
// production builds — the worker is emitted by GenerateSW there, and a worker in
// the dev server would interfere with HMR.
const registerServiceWorker = (): void => {
  if (
    process.env.NODE_ENV !== 'production' ||
    !('serviceWorker' in navigator)
  ) {
    return;
  }
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .catch(() => undefined);
  });
};

registerServiceWorker();
